import React, { useEffect, useMemo, useRef, useState } from 'react';

import { contactApis, dashboardContactApis } from '@apis/next/contactApis';
import addGraySvg from '@assets/img/icons/add-gray.svg';
import addSvg from '@assets/img/icons/add.svg';
import deleteSvg from '@assets/img/icons/delete.svg';
import horizontalSwitchSvg from '@assets/img/icons/horizontal-switch.svg';
import infoSvg from '@assets/img/icons/info.svg';
import removeSvg from '@assets/img/icons/remove.svg';
import { SimpleTextInput } from '@components/admin/Forms';
import { TextInput } from '@components/admin/Forms';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogContent,
  ActionableDialogContentDivider,
  ActionableDialogTitle,
} from '@components/Dialog';
import { ErrorMessage } from '@components/Forms/ErrorMessage';
import { QueryContainer } from '@components/QueryContainer';
import { useBookingCreateContext } from '@components/utilities/BookingInquiry/BookingCreateContext';
import { CargosPortNameHelpersDialog } from '@components/utilities/BookingInquiry/CargosPortNameHelpersDialog';
import { ContactFilter } from '@components/utilities/BookingInquiry/ContactFilter';
import { ContactForm } from '@components/utilities/Contact/ContactForm';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { getReceivingCityOptionInlineDisplay, receivingCityOptions } from '@data/cities';
import IconButton from '@material-ui/core/IconButton';
import Popper from '@material-ui/core/Popper';
import {
  bookingInquiryCargosPickupAddressInput,
  BookingInquiryCargosPickupAddressInput,
  Cargo2,
  getDefaultCargoForm,
  getSummaryOfCargo2,
} from '@models/BookingInquiry';
import { ContactForm as TContactForm, contactFormInitialValues } from '@models/Contact';
import { ContactSearchQuery } from '@models/next/Contact';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createValidate, formStrings, joinKeyPath, useErrorMessage } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { iota, optionalToUndefined } from '@utils/types';
import clsx from 'clsx';
import Color from 'color';
import { Form, Formik, useField } from 'formik';
import sum from 'lodash-es/sum';
import useHoverDirty from 'react-use/lib/useHoverDirty';
import { unwrap } from 'tyrann-io';

const strings = createStrings({
  ja: {
    addPickupAddress: `${bookingInquiryStrings.pickupAddress}を選択する`,
    choosePickupAddress: `${bookingInquiryStrings.pickupAddress}を選択する`,
    inputPickupAddressPrompt: `${bookingInquiryStrings.pickupAddress}を入力する`,
    choosePickupAddressPrompt: `${bookingInquiryStrings.pickupAddress}を選択してください`,
    ok: '次へ',
    canSelectMultiple: `＊複数の${bookingInquiryStrings.pickupAddress}を選択するのは可能です。`,
    selectShipperPrompt: `「SHIPPER（輸出者）情報」で SHIPPER を選択してください。`,
    createPickupAddress: `${bookingInquiryStrings.pickupAddress}を追加する`,
    createPickupAddressTitle: `${bookingInquiryStrings.pickupAddress}情報を入力してください`,
    createPickupAddressOK: 'OK',
    aboutPickupAddress: () => (
      <>
        {bookingInquiryStrings.pickupAddress}とSHIPPERの違いは？
        <br />
        SHIPPERは輸出者の会社情報です。{bookingInquiryStrings.pickupAddress}
        は、輸出者の荷物をピックアップする場所です。一般的には、中国国内の工場です。
      </>
    ),
    addCargo: '出荷物を追加',
    addPickupAddressPrompt: `${bookingInquiryStrings.pickupAddress}を選んでください`,
    selectPortPlaceholder: '倉庫を選んでください',
    cannotChangeCargosPortName: `ルートは変更できないので、${bookingInquiryStrings.warehouse}は変更できないこととなっています。`,
    english: '英語',
    cargoNumHintString:
      '梱包後の貨物個数をご記入ください。複数の貨物をパレットなどににまとめている場合は荷姿で数えてください。',
    cargosPortNamePrompt: '中国国内の荷受倉庫を選んでください',
    cargosPortNameInputHelpers: (onClickOpen: () => void) => (
      <>
        中国国内倉庫の選び方：
        <br />
        輸送時間とコストを節約するために、出荷元に近い倉庫を選択することをお勧めします。D2D倉庫の場所を確認するには、
        <a onClick={onClickOpen}>こちら</a>から確認してください。
        <br />
      </>
    ),
    computeByLWH: 'LWH',
    computeByWV: '総重',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  cargosInputContainer: {
    padding: '9px 14px',
  },
  addPickupAddressButton: {
    ...common.flexCenter,
    ...common.middleText,
    ...common.textPrimary,
    ...common.fontWeight600,
    ...common.baseButton,
    flexShrink: 0,
    width: 250,
    height: 168,
    borderRadius: 4,
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  pickupAddressDisplay: {
    ...common.middleText,
    flexShrink: 0,
    width: 250,
    height: 168,
    borderRadius: 4,
    border: `1px solid ${theme.palette.grey[300]}`,
    padding: `12px 17px`,
  },
  pickupAddressContainer: {
    ...common.flexInline,
    overflowX: 'auto',
    columnGap: '1rem',
  },
  cargosListTable: {
    ...common.middleText,
    display: 'grid',
    overflowX: 'scroll',
    overflowY: 'visible',
    gridTemplateColumns:
      'auto 2fr minmax(max-content, 1fr) minmax(max-content, 1fr) minmax(max-content, 1fr) max-content',
    '$hasPickups &': {
      gridTemplateColumns:
        'minmax(120px, 0.9fr) 2fr minmax(max-content, 1fr) minmax(max-content, 1fr) minmax(max-content, 1fr) max-content',
    },
    '& > *[role="head"]': {
      ...common.textGray500,
      ...common.fontWeight600,
      backgroundColor: Color(theme.palette.primary.main).alpha(0.05).toString(),
    },
    '& > div[role="data"]': {
      paddingTop: 16,
      paddingLeft: 9,
      paddingRight: 9,
    },
    '& > div': {
      paddingTop: 10,
      paddingBottom: 10,
      paddingLeft: 9,
      paddingRight: 9,
    },
  },
  cargosSummaryContainer: {
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.middleText,
    padding: '1rem',
    backgroundColor: Color(theme.palette.primary.main).alpha(0.05).toString(),
    [theme.breakpoints.down('xs')]: {
      ...common.flexColumn,
      alignItems: 'flex-start',
      rowGap: '0.3rem',
    },
  },
  actionButton: {
    ...common.baseButton,
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.textPrimary,
  },
  lwhSplit: {
    ...common.textGray500,
    paddingTop: 4,
    paddingLeft: 4,
    paddingRight: 4,
  },
  label: {
    width: 150,
  },
  receivingCityGroupInput: {},
  cargoNumHintContainer: {
    ...common.baseButton,
    display: 'inline',
    position: 'relative',
    marginLeft: '0.5rem',
    top: 1,
  },
  hintContainer: {
    ...common.exSmallerText,
    ...common.textGray700,
    marginLeft: '0.5rem',
    marginRight: '0.5rem',
    padding: 9,
    lineHeight: 1.75,
    backgroundColor: '#F2F7F8',
    width: 300,
    border: `1px solid ${theme.palette.primary.main}`,
  },
  cargosPortNameInputCreateContainer: {
    ...common.middleText,
    ...common.textGray700,
    display: 'grid',
    gridTemplateColumns: '400px 1fr',
    columnGap: 40,
    paddingTop: 12,
    paddingBottom: 12,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      rowGap: '1rem',
    },
  },
  cargosPortNameInputHelpers: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: 4,
    '& a': {
      ...common.link,
      textDecoration: 'underline',
    },
  },
  cargosPortNameInputAdminEditContainer: {
    ...common.middleText,
    ...common.textGray700,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: 20,
    paddingTop: 12,
    paddingBottom: 12,
    '$hasPickups &': {
      columnGap: 80,
    },
  },
  LWH: {},
  WV: {},
  hasPickups: {},
}));

export type CargosInputVariant = 'create' | 'adminEdit';
export interface CargosInputProps {
  name: string;
  isDashboard: boolean;
  canEdit?: boolean;
  variant?: CargosInputVariant;
  pickupAddresses?: TContactForm[];
}

const PickupAddressDisplay: React.FC<{
  index: number;
  contact: TContactForm;
  onClick?: () => void;
  onDelete?: () => void;
}> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { index, contact, onClick, onDelete } = props;

  return (
    <div className={clsx(classes.pickupAddressDisplay)}>
      <div
        className={clsx(classes.textPrimary, classes.fontWeight600Important)}
        style={{ marginBottom: 14 }}
      >
        <span onClick={onClick} className={clsx(!!onClick && classes.baseButton)}>
          {bookingInquiryStrings.pickupAddress} {index + 1}
        </span>
        <img
          src={removeSvg}
          alt="Remove"
          style={{ float: 'right', position: 'relative', top: 4 }}
          className={clsx(!!onDelete && classes.baseButton)}
          onClick={onDelete}
        />
      </div>

      <div
        className={clsx(classes.textBlack, classes.fontWeight600Important, classes.preEllipsis)}
        style={{ marginBottom: 6 }}
      >
        {contact.english_name}
      </div>

      <div className={clsx(classes.textGray900, classes.preEllipsis)} style={{ marginBottom: 4 }}>
        {contact.zip_code}
      </div>

      <div className={clsx(classes.textGray900, classes.preEllipsis)} style={{ marginBottom: 4 }}>
        {contact.address1 || '-'}
      </div>
      <div className={clsx(classes.textGray900, classes.preEllipsis)}>
        {contact.address2 || '-'}
      </div>
    </div>
  );
};

const AddPickupAddressButton: React.FC<{ hasShipperInput: boolean; onClick: () => void }> = (
  props,
) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { hasShipperInput, onClick } = props;

  return (
    <div className={classes.addPickupAddressButton} onClick={onClick}>
      <div>
        <img
          src={addSvg}
          alt="Add"
          style={{ verticalAlign: 'middle', position: 'relative', top: -1, marginRight: 6 }}
        />
        {hasShipperInput ? strings.inputPickupAddressPrompt : strings.addPickupAddress}
      </div>
    </div>
  );
};

const CargoRow: React.FC<{
  index: number;
  name: string;
  hasPickups: boolean;
  pickupAddressCount: number;
  canEdit: boolean;
}> = ({ index, name, hasPickups, pickupAddressCount, canEdit }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [cargoField] = useField<Cargo2>(name);

  const [cargosField, , cargosHelpers] = useField<Cargo2[]>('cargos2');

  return (
    <>
      {!hasPickups && (
        <div style={{ paddingLeft: 20 }} role="data">
          {index + 1}{' '}
        </div>
      )}
      {hasPickups && (
        <div style={{ paddingLeft: 14 }}>
          <SimpleTextInput
            name={joinKeyPath(name, 'pickup_address_index')}
            textInputWidth={60}
            hideError
            choose
            disabled={!canEdit}
            options={[
              {
                value: 0,
                label: '-',
              },
              ...iota(pickupAddressCount, 1).map((label) => ({
                value: label,
                label: `${bookingInquiryStrings.pickupAddress} ${label}`,
              })),
            ]}
          />
        </div>
      )}

      <div>
        <SimpleTextInput name={joinKeyPath(name, 'name')} textInputFullWidth disabled={!canEdit} />
      </div>

      {cargoField.value.tag === 'WV' && (
        <>
          <div>
            <SimpleTextInput
              name={joinKeyPath(name, 'num')}
              textInputFullWidth
              hideError
              disabled={!canEdit}
            />
          </div>
          <div>
            <SimpleTextInput
              name={joinKeyPath(name, 'total_weight')}
              textInputFullWidth
              hideError
              disabled={!canEdit}
            />
          </div>
          <div>
            <SimpleTextInput
              name={joinKeyPath(name, 'total_volume')}
              textInputFullWidth
              hideError
              disabled={!canEdit}
            />
          </div>
        </>
      )}

      {cargoField.value.tag === 'LWH' && (
        <>
          <div>
            <SimpleTextInput
              name={joinKeyPath(name, 'num')}
              textInputFullWidth
              hideError
              disabled={!canEdit}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr auto 1fr' }}>
            <SimpleTextInput
              name={joinKeyPath(name, 'length')}
              textInputFullWidth
              hideError
              disabled={!canEdit}
            />
            <div className={classes.lwhSplit}>{':'}</div>
            <SimpleTextInput
              name={joinKeyPath(name, 'width')}
              textInputFullWidth
              hideError
              disabled={!canEdit}
            />
            <div className={classes.lwhSplit}>{':'}</div>
            <SimpleTextInput
              name={joinKeyPath(name, 'height')}
              textInputFullWidth
              hideError
              disabled={!canEdit}
            />
          </div>
          <div>
            <SimpleTextInput
              name={joinKeyPath(name, 'weight')}
              textInputFullWidth
              hideError
              disabled={!canEdit}
            />
          </div>
        </>
      )}

      <div
        className={clsx(classes.flexInline, classes.justifyEnd)}
        style={{ columnGap: '1rem', paddingRight: 20 }}
      >
        {canEdit && (
          <button
            type="button"
            onClick={() => {
              cargosHelpers.setValue(cargosField.value.filter((v) => v.id !== cargoField.value.id));
            }}
            className={clsx(classes.flexColumn, classes.textPrimary)}
            style={{ paddingTop: 5 }}
          >
            <img src={deleteSvg} alt="Delete" />
          </button>
        )}
      </div>
    </>
  );
};

const CargoNumHint: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const infoRef = useRef<HTMLImageElement>(null);

  const isHoveringInfo = useHoverDirty(infoRef);

  const [anchorEl, setAnchorEl] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    setAnchorEl(infoRef.current);
  }, [isHoveringInfo]);

  return (
    <div className={classes.cargoNumHintContainer}>
      <img alt="Info" src={infoSvg} ref={infoRef} />

      <Popper open={isHoveringInfo} anchorEl={anchorEl} placement="right-start">
        <div className={classes.hintContainer}>{strings.cargoNumHintString}</div>
      </Popper>
    </div>
  );
};

export const CargosListInput: React.FC<{
  canEdit: boolean;
  canAddCargo?: boolean;
  cannotAddCargoReason?: string;
  pickupAddressCount: number;
  isDashboard?: boolean;
}> = ({ canEdit, canAddCargo = true, cannotAddCargoReason, pickupAddressCount, isDashboard }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [hasPickupsField] = useField<'true' | 'false'>('has_pickups');

  const hasPickups = hasPickupsField.value === 'true';

  const [cargosField, , cargosHelpers] = useField<Cargo2[]>('cargos2');

  const tag = cargosField.value[0]?.tag ?? 'WV';

  const onAddCargo = () => {
    cargosHelpers.setValue([
      ...cargosField.value,
      {
        ...getDefaultCargoForm(tag),
        pickup_address_index: hasPickups ? 1 : 0,
      },
    ]);
  };

  const onChangeMeasure = () => {
    const nextMeasure = tag === 'WV' ? 'LWH' : 'WV';

    if (cargosField.value.length === 0) {
      cargosHelpers.setValue([getDefaultCargoForm(nextMeasure)]);
    } else {
      cargosHelpers.setValue(
        cargosField.value.map((v) => ({
          ...getDefaultCargoForm(nextMeasure),
          id: v.id,
          name: v.name,
          pickup_address_index: v.pickup_address_index,
        })),
      );
    }
  };

  const renderHead = () => (
    <>
      <div role="head" style={{ paddingLeft: 20 }}>
        {hasPickups ? bookingInquiryStrings.pickupAddress : 'NO'}
      </div>
      <div role="head">
        {bookingInquiryStrings.cargoName} ({strings.english})
      </div>
      <div role="head">
        {bookingInquiryStrings.cargoNum}
        <CargoNumHint />
      </div>

      {tag === 'WV' && (
        <>
          <div role="head">{bookingInquiryStrings.totalWeight}</div>
          <div role="head">{bookingInquiryStrings.totalVolume}</div>
        </>
      )}

      {tag === 'LWH' && (
        <>
          <div role="head">{bookingInquiryStrings.cargoSize}</div>
          <div role="head">{bookingInquiryStrings.weight}</div>
        </>
      )}

      {!isDashboard && (
        <div
          role="head"
          style={{ paddingRight: 20, paddingLeft: 0 }}
          className={clsx(classes.baseButton, classes.textPrimaryImportant)}
          onClick={onChangeMeasure}
        >
          {canEdit && (
            <span style={{ position: 'relative', top: -1 }}>
              <img
                src={horizontalSwitchSvg}
                alt="Switch Icon"
                style={{ marginRight: 8, position: 'relative', top: 3 }}
              />
              {tag === 'WV'
                ? bookingInquiryStrings.computeByLWH
                : bookingInquiryStrings.computeByWV}
            </span>
          )}
        </div>
      )}

      {isDashboard && (
        <div
          role="head"
          style={{ paddingRight: 20, paddingLeft: 16 }}
          className={clsx(classes.baseButton, classes.textPrimaryImportant)}
          onClick={onChangeMeasure}
        >
          {canEdit && (
            <button
              className={clsx(classes.flexColumn, classes.itemsCenter)}
              style={{ position: 'relative', left: -2, top: -2 }}
              type="button"
            >
              <img
                src={horizontalSwitchSvg}
                alt="Switch Icon"
                style={{ position: 'relative', marginBottom: 6 }}
              />
              <span style={{ fontSize: 8, lineHeight: 0 }}>
                {tag === 'WV' ? strings.computeByLWH : strings.computeByWV}
              </span>
            </button>
          )}
        </div>
      )}
    </>
  );

  const renderInputs = () => {
    return cargosField.value.map((cargo, index) => (
      <CargoRow
        key={cargo.id}
        name={joinKeyPath('cargos2', index)}
        index={index}
        hasPickups={hasPickups}
        pickupAddressCount={pickupAddressCount}
        canEdit={canEdit}
      />
    ));
  };

  const renderSummary = () => {
    const summaries = cargosField.value.map(getSummaryOfCargo2);

    const unit = sum(summaries.map((s) => s.unit));
    const cbm = sum(summaries.map((s) => s.cbm));
    const kg = sum(summaries.map((s) => s.kg));

    return (
      <div className={clsx(classes.cargosSummaryContainer)}>
        {canEdit && (
          <div
            className={clsx(classes.actionButton, !canAddCargo && classes.textGray500Important)}
            onClick={canAddCargo ? onAddCargo : undefined}
          >
            <IconButton disabled={!canAddCargo} title={cannotAddCargoReason}>
              <img src={canAddCargo ? addSvg : addGraySvg} alt="Add" />
            </IconButton>
            {strings.addCargo}
          </div>
        )}
        <div style={{ flex: 1 }} />
        <div className={classes.textGray700}>
          Shipment Total: {unit ?? '-'} UNITS, {cbm?.toFixed(3) ?? '-'} CBM, {kg?.toFixed(3) ?? '-'}{' '}
          KGS
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className={clsx(classes.cargosListTable)}>
        {renderHead()}
        {renderInputs()}
      </div>
      {cargosField.value.length === 0 && <div style={{ height: 10 }} />}
      {renderSummary()}
    </div>
  );
};

const CargosPickupAddressManualInput: React.FC<{
  initialValues: BookingInquiryCargosPickupAddressInput;
  onClose: () => void;
  onSubmit: (values: BookingInquiryCargosPickupAddressInput) => void;
}> = ({ initialValues, onClose, onSubmit }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <ActionableDialog open onClose={onClose} size="lg" muiDialogProps={{ disablePortal: false }}>
      <Formik
        initialValues={initialValues}
        validate={createValidate(bookingInquiryCargosPickupAddressInput)}
        onSubmit={(values) => {
          onSubmit(unwrap(bookingInquiryCargosPickupAddressInput, values));
          onClose();
        }}
      >
        <Form>
          <ActionableDialogTitle>{strings.inputPickupAddressPrompt}</ActionableDialogTitle>
          <ActionableDialogContent>
            <ContactForm name="contact" zipcodeRequired contactType="pickup_address" />
          </ActionableDialogContent>

          <ActionableDialogContentDivider />

          <div style={{ padding: '1rem 24px' }} className={classes.flexCenter}>
            <Button
              color="primary"
              text={formStrings.ok}
              size="small"
              shape="round"
              data-testid="close-contact-create"
              type="submit"
              style={{ padding: '2px 36px' }}
            />
          </div>
        </Form>
      </Formik>
    </ActionableDialog>
  );
};

export const CargosPortNameInput: React.FC<{ name: string; variant?: CargosInputVariant }> = ({
  name,
  variant,
}) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [dialogOpen, setDialogOpen] = useState(false);

  if (variant === 'create') {
    return (
      <div className={classes.cargosPortNameInputCreateContainer}>
        <CargosPortNameHelpersDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />

        <div>
          {variant === 'create' && (
            <>
              <div className={clsx(classes.middleText, classes.flexInline)}>
                {strings.cargosPortNamePrompt}
                <span className={classes.textError}>*</span>
              </div>

              <div style={{ height: 10 }} />
              <TextInput
                name={joinKeyPath(name, 'cargos_port_name')}
                noLabel
                placeholder={strings.selectPortPlaceholder}
                formClasses={{
                  label: classes.label,
                }}
                classes={{
                  input: classes.receivingCityGroupInput,
                }}
                choose
                options={receivingCityOptions.map((g) => ({
                  value: g.name,
                  label: getReceivingCityOptionInlineDisplay(g),
                }))}
              />
            </>
          )}
        </div>
        <div className={classes.cargosPortNameInputHelpers}>
          <img src={infoSvg} style={{ position: 'relative', top: 3 }} />

          <div>{strings.cargosPortNameInputHelpers(() => setDialogOpen(true))}</div>
        </div>
      </div>
    );
  } else {
    return (
      <div className={classes.cargosPortNameInputAdminEditContainer}>
        <TextInput
          name={joinKeyPath(name, 'cargos_port_name')}
          label={bookingInquiryStrings.portName}
          placeholder={strings.selectPortPlaceholder}
          choose
          options={receivingCityOptions.map((g) => ({
            value: g.name,
            label: getReceivingCityOptionInlineDisplay(g),
          }))}
          disabled
          title={strings.cannotChangeCargosPortName}
        />

        <TextInput
          name={joinKeyPath(name, 'move_in_date')}
          label={bookingInquiryStrings.moveInDate2}
          type="date"
        />
      </div>
    );
  }
};

export const CargosInput: React.FC<CargosInputProps> = ({
  name,
  variant = 'create',
  pickupAddresses,
  canEdit = true,
}) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { isDashboard } = useBookingCreateContext();

  const errorMessage = useErrorMessage(joinKeyPath(name, 'cargos2'));

  const [cargosValue, , cargosHelpers] = useField<Cargo2[]>(joinKeyPath(name, 'cargos2'));

  const [hasPickupsField] = useField<'true' | 'false'>(joinKeyPath(name, 'has_pickups'));

  const [pickupAddressManualInputOpen, setPickupAddressManualInputOpen] = useState(false);
  const [pickupAddressManualInputInitialValues, setPickupAddressManualInputInitialValues] =
    useState<BookingInquiryCargosPickupAddressInput | undefined>();

  const [shipperField] = useField<number | undefined>(joinKeyPath(name, 'shipper'));
  const [shipperInputField] = useField<TContactForm | undefined>(
    joinKeyPath(name, 'shipper_input'),
  );

  const [cargosPickupAddressInputsField, , cargosPickupAddressInputsHelpers] = useField<
    BookingInquiryCargosPickupAddressInput[]
  >(joinKeyPath(name, 'cargos_pickup_address_inputs'));

  const [cargosPickupAddressesField, , cargosPickupAddressesHelpers] = useField<
    number[] | undefined
  >(joinKeyPath(name, 'cargos_pickup_addresses'));

  const [cargosPickupAddressInputsAction, setCargosPickupAddressInputsAction] = useState<
    'create' | 'edit'
  >('create');

  const hasShipperInput = !!shipperInputField.value; // Is shipper manually input

  const [pickupFilterOpen, setPickupFilterOpen] = useState(false);
  // const [createPickupAddressDialogOpen, setCreatePickupAddresssDialogOpen] = useState(false);

  const [currentSelectedIds, setCurrentSelectedIds] = useState<number[]>([]);

  const [queryParams, setQueryParams] = useState<ContactSearchQuery>({
    company_name: '',
    principal_name: '',
  });

  const pickupAddressQueryParams = [
    {
      queries: {
        ...queryParams,
        ordering: ['-use_count'],
        contact_type: 'pickup_address',
        owner_id: shipperField.value!,
      } satisfies ContactSearchQuery,
    },
    { enabled: !!shipperField.value && variant !== 'adminEdit' },
  ] as const;

  const pickupAddressQuery = isDashboard
    ? dashboardContactApis.useGet('/dashboard/contact', ...pickupAddressQueryParams)
    : contactApis.useGet('/contact', ...pickupAddressQueryParams);

  const displayedPickupAddresses: TContactForm[] = useMemo(() => {
    if (hasShipperInput) {
      return cargosPickupAddressInputsField.value.map((p) => p.contact);
    } else {
      return (
        pickupAddresses ??
        cargosPickupAddressesField.value
          ?.filter((id) => pickupAddressQuery?.data?.some((p) => p.id === id))
          .map((id) => pickupAddressQuery?.data?.find((p) => p.id === id)!)
          .map(optionalToUndefined) ??
        []
      );
    }
  }, [
    cargosPickupAddressesField.value,
    pickupAddressQuery.data,
    pickupAddresses,
    hasShipperInput,
    cargosPickupAddressInputsField.value,
  ]);

  const onClosePickupDialog = () => {
    setPickupFilterOpen(false);

    cargosPickupAddressesHelpers.setValue([
      ...(cargosPickupAddressesField.value ?? []),
      ...currentSelectedIds,
    ]);
  };

  const renderChoosePickupDialog = () => {
    return (
      <ActionableDialog
        open={pickupFilterOpen}
        onClose={onClosePickupDialog}
        size="lg"
        muiDialogProps={{
          disablePortal: false,
        }}
      >
        <ActionableDialogTitle>
          <span className={clsx(classes.bigText, classes.fontWeight500)}>
            {strings.choosePickupAddressPrompt}
          </span>
          <span
            className={clsx(classes.normalText, classes.fontWeight500)}
            style={{ paddingLeft: '1rem' }}
          >
            {strings.canSelectMultiple}
          </span>
        </ActionableDialogTitle>
        <ActionableDialogContent>
          <QueryContainer
            queries={[pickupAddressQuery]}
            style={{
              height: 478,
            }}
          >
            {() => (
              <ContactFilter
                disabledIds={cargosPickupAddressesField.value}
                selectedIds={currentSelectedIds}
                onChangeSelectedIds={setCurrentSelectedIds}
                contacts={pickupAddressQuery
                  .data!.filter(
                    (s) => s.company_name?.includes(queryParams.company_name ?? '') ?? true,
                  )
                  .filter(
                    (s) => s.principal_name?.includes(queryParams.principal_name ?? '') ?? true,
                  )}
                title={bookingInquiryStrings.pickupAddress}
                query={queryParams}
                onChangeQuery={setQueryParams}
                onAddContact={() =>
                  window.open(
                    `${url('/utilities/Contact/Shippers/ShipperDetails')}?id=${
                      shipperField.value
                    }&action=createOwnedContact`,
                  )
                }
                actions={isDashboard ? 'none' : 'modify'}
                isEnglishNameRequired={!isDashboard}
                isZipcodeRequired
                editUrl={(contact) =>
                  (isDashboard
                    ? undefined
                    : `${url('/utilities/Contact/Shippers/ShipperDetails')}?id=${
                        contact.owner
                      }&action=editWithScrollBottom`) || ''
                }
              />
            )}
          </QueryContainer>
        </ActionableDialogContent>
        <ActionableDialogContentDivider />
        <div style={{ padding: '1rem 24px' }}>
          <Button
            color="primary"
            text={strings.ok}
            size="small"
            onClick={onClosePickupDialog}
            type="button"
            data-testid="close-contact-filter"
            style={{ width: '100%' }}
          />
        </div>
      </ActionableDialog>
    );
  };

  return (
    <div
      className={clsx(
        classes.cargosInputContainer,
        hasPickupsField.value === 'true' && classes.hasPickups,
      )}
    >
      {hasPickupsField.value === 'true' && (
        <>
          {!shipperField.value && !hasShipperInput && variant === 'create' && (
            <div className={clsx(classes.textError, classes.middleText)}>
              {strings.selectShipperPrompt}
            </div>
          )}

          {!!shipperField.value && renderChoosePickupDialog()}

          {pickupAddressManualInputOpen && (
            <CargosPickupAddressManualInput
              initialValues={pickupAddressManualInputInitialValues!}
              onClose={() => setPickupAddressManualInputOpen(false)}
              onSubmit={(values) => {
                switch (cargosPickupAddressInputsAction) {
                  case 'create':
                    return cargosPickupAddressInputsHelpers.setValue([
                      ...cargosPickupAddressInputsField.value,
                      values,
                    ]);
                  case 'edit':
                    return cargosPickupAddressInputsHelpers.setValue(
                      cargosPickupAddressInputsField.value.map((v) =>
                        v.id === values.id ? values : v,
                      ),
                    );
                }
              }}
            />
          )}

          {variant === 'adminEdit' && (
            <div style={{ width: 300, marginBottom: '1rem' }}>
              <TextInput
                name={joinKeyPath(name, 'pickup_date')}
                label={bookingInquiryStrings.pickupDate}
                type="date"
              />
            </div>
          )}

          {(shipperField.value || hasShipperInput || variant === 'adminEdit') && (
            <div className={classes.pickupAddressContainer}>
              {variant === 'create' && (
                <AddPickupAddressButton
                  hasShipperInput={hasShipperInput}
                  onClick={() => {
                    if (hasShipperInput) {
                      setPickupAddressManualInputOpen(true);
                      setPickupAddressManualInputInitialValues({
                        id: Math.floor(Math.random() * 10000),
                        contact: contactFormInitialValues,
                      });
                      setCargosPickupAddressInputsAction('create');
                    } else {
                      setCurrentSelectedIds([]);
                      setPickupFilterOpen(true);
                    }
                  }}
                />
              )}

              {displayedPickupAddresses.map((contact, index) => (
                <PickupAddressDisplay
                  key={index}
                  index={index}
                  contact={contact}
                  onClick={
                    hasShipperInput
                      ? () => {
                          setPickupAddressManualInputOpen(true);
                          setPickupAddressManualInputInitialValues(
                            cargosPickupAddressInputsField.value[index],
                          );
                          setCargosPickupAddressInputsAction('edit');
                        }
                      : undefined
                  }
                  onDelete={() => {
                    if (hasShipperInput) {
                      cargosPickupAddressInputsHelpers.setValue(
                        cargosPickupAddressInputsField.value.filter((_, i) => i !== index),
                      );
                    } else {
                      cargosPickupAddressesHelpers.setValue(
                        (cargosPickupAddressesField.value ?? []).filter((_, i) => i !== index),
                      );
                    }

                    cargosHelpers.setValue(
                      cargosValue.value.map((c) => {
                        if (
                          c.pickup_address_index === undefined ||
                          c.pickup_address_index - 1 < index
                        ) {
                          return c;
                        }
                        if (c.pickup_address_index - 1 === index) {
                          return {
                            ...c,
                            pickup_address_index: 0,
                          };
                        }
                        return {
                          ...c,
                          pickup_address_index: c.pickup_address_index - 1,
                        };
                      }),
                    );
                  }}
                />
              ))}
            </div>
          )}
          <ErrorMessage
            name={joinKeyPath(name, 'cargos_pickup_addresses')}
            style={{ paddingTop: '0.5rem' }}
          />
          <div style={{ height: variant === 'adminEdit' ? 16 : 50 }} />
        </>
      )}

      {hasPickupsField.value === 'false' && (
        <>
          <CargosPortNameInput name={name} variant={variant} />
        </>
      )}

      <CargosListInput
        pickupAddressCount={displayedPickupAddresses.length}
        canEdit={canEdit}
        isDashboard={isDashboard}
        {...(hasPickupsField.value === 'true' && displayedPickupAddresses.length === 0
          ? {
              canAddCargo: false,
              cannotAddCargoReason: strings.addPickupAddressPrompt,
            }
          : undefined)}
      />

      {typeof errorMessage === 'string' && (
        <div className={clsx(classes.textError, classes.smallerText)} style={{ padding: '1rem' }}>
          {errorMessage}
        </div>
      )}
    </div>
  );
};
